import React from "react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { StatusDot } from "./sshProxyUi";
import { livenessState } from "./sshProxyModel";

// Right rail — the prototype's summary panel and architecture diagram survive;
// the rows change. Liveness and administrative status are different things and
// must not share a row: status is what an admin set in the console; liveness is
// observed from the heartbeat. A proxy can be enabled and not running, or
// running and disabled.
function Row({ label, children }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, padding: "9px 0", borderBottom: `1px solid ${AN_COLORS.line2}` }}>
      <span style={{ fontSize: 11.5, color: AN_COLORS.muted, fontFamily: AN_FONT, whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ fontSize: 12.5, color: AN_COLORS.ink, fontFamily: AN_FONT, textAlign: "right" }}>{children}</span>
    </div>
  );
}

export default function RightRail({ state, jumpServerId }) {
  const live = livenessState(state.heartbeatSeen ? state.lastSeenAt : null);
  const mono = { fontFamily: AN_MONO, fontSize: 12 };

  return (
    <div style={{ position: "sticky", top: 18, display: "grid", gap: 14 }}>
      {/* Summary */}
      <div style={{ background: AN_COLORS.surface, border: `1px solid ${AN_COLORS.line}`, borderRadius: 10, padding: "14px 16px" }}>
        <div style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 8 }}>Summary</div>

        <Row label="Gateway">{state.proxyName || <span style={{ color: AN_COLORS.muted }}>Unnamed</span>}</Row>
        <Row label="Public IP"><span style={mono}>{state.publicIp || "—"}</span></Row>
        <Row label="Listen port"><span style={mono}>{state.listenPort || 2222}</span></Row>

        <Row label="Liveness">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <StatusDot tone={live.tone} />
            {live.label}
          </span>
        </Row>
        <Row label="Version"><span style={mono}>{state.proxyVersion || "—"}</span></Row>
        <Row label="Listening on"><span style={mono}>{state.proxyListen || "—"}</span></Row>
        {/* Observed by the server, not reported by the proxy — this is how an
            admin confirms the heartbeat comes from the box they think it does. */}
        <Row label="Seen from"><span style={mono}>{state.lastSeenIp || "—"}</span></Row>

        <Row label="Policy">{state.policy?.saved ? <span style={{ color: AN_COLORS.success }}>Attached</span> : <span style={{ color: AN_COLORS.muted }}>None</span>}</Row>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, padding: "9px 0" }}>
          <span style={{ fontSize: 11.5, color: AN_COLORS.muted, fontFamily: AN_FONT }}>Recording</span>
          <span style={{ fontSize: 12.5, color: state.recording ? AN_COLORS.success : AN_COLORS.muted }}>{state.recording ? "On" : "Off"}</span>
        </div>
      </div>

      {/* Architecture — the gateway is a systemd binary on the jump host. That is
          the detail people most often get wrong, so it is drawn explicitly. */}
      <div style={{ background: AN_COLORS.surface, border: `1px solid ${AN_COLORS.line}`, borderRadius: 10, padding: "14px 16px" }}>
        <div style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 12 }}>Architecture</div>
        <svg viewBox="0 0 260 210" width="100%" style={{ display: "block" }} fontFamily={AN_FONT}>
          <defs>
            <marker id="ssharrow" markerWidth="7" markerHeight="7" refX="5.5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill={AN_COLORS.ink3} />
            </marker>
          </defs>

          {/* Operator */}
          <rect x="8" y="14" width="90" height="40" rx="7" fill={AN_COLORS.tint} stroke={AN_COLORS.line} />
          <text x="53" y="32" textAnchor="middle" fontSize="10.5" fontWeight="600" fill={AN_COLORS.ink}>Operator</text>
          <text x="53" y="45" textAnchor="middle" fontSize="8.5" fill={AN_COLORS.muted}>8h certificate</text>

          {/* Jump host containing proxy (systemd) */}
          <rect x="150" y="6" width="104" height="86" rx="8" fill="none" stroke={AN_COLORS.line} strokeDasharray="3 3" />
          <text x="202" y="18" textAnchor="middle" fontSize="7.5" fill={AN_COLORS.muted} letterSpacing="0.5">JUMP HOST</text>
          <rect x="160" y="24" width="84" height="42" rx="7" fill={AN_COLORS.primaryL} stroke={AN_COLORS.primary} />
          <text x="202" y="41" textAnchor="middle" fontSize="10" fontWeight="600" fill={AN_COLORS.primaryD}>gateway :2222</text>
          <text x="202" y="53" textAnchor="middle" fontSize="7.5" fill={AN_COLORS.primaryD}>systemd binary</text>
          <text x="202" y="80" textAnchor="middle" fontSize="8" fill={AN_COLORS.muted}>session key →</text>

          {/* Authnull + phone */}
          <rect x="150" y="104" width="104" height="34" rx="7" fill={AN_COLORS.bg} stroke={AN_COLORS.line} />
          <text x="202" y="118" textAnchor="middle" fontSize="9" fontWeight="600" fill={AN_COLORS.ink}>Authnull</text>
          <text x="202" y="130" textAnchor="middle" fontSize="7.5" fill={AN_COLORS.muted}>approves · releases key</text>

          {/* Target */}
          <rect x="8" y="150" width="90" height="40" rx="7" fill={AN_COLORS.tint} stroke={AN_COLORS.line} />
          <text x="53" y="168" textAnchor="middle" fontSize="10.5" fontWeight="600" fill={AN_COLORS.ink}>Target host</text>
          <text x="53" y="181" textAnchor="middle" fontSize="8.5" fill={AN_COLORS.muted}>per-session key</text>

          {/* connection 1: operator → proxy */}
          <line x1="98" y1="34" x2="158" y2="42" stroke={AN_COLORS.ink3} strokeWidth="1.3" markerEnd="url(#ssharrow)" />
          <text x="126" y="30" textAnchor="middle" fontSize="7.5" fill={AN_COLORS.muted}>conn 1</text>

          {/* gateway → Authnull. HTTPS, not SSH: a policy decision, the push
              approval, and the grant key's one-time release. The two SSH
              connections are operator→gateway and gateway→target. */}
          <line x1="202" y1="66" x2="202" y2="102" stroke={AN_COLORS.ink3} strokeWidth="1.1" markerEnd="url(#ssharrow)" strokeDasharray="2 2" />
          <text x="208" y="88" textAnchor="start" fontSize="7" fill={AN_COLORS.muted}>https</text>

          {/* connection 2: gateway → target, with the per-session grant key */}
          <line x1="160" y1="70" x2="98" y2="160" stroke={AN_COLORS.ink3} strokeWidth="1.3" markerEnd="url(#ssharrow)" />
          <text x="120" y="118" textAnchor="middle" fontSize="7.5" fill={AN_COLORS.muted}>conn 2</text>
        </svg>
        <div style={{ fontSize: 11, color: AN_COLORS.muted, lineHeight: 1.5, marginTop: 4 }}>
          Two separate SSH connections. The operator reaches the gateway with an 8-hour certificate issued by the
          console, then approves the session by push. The gateway reaches the target with a key issued for that
          session, released once after approval and only for a host and account a policy names. The dashed leg is
          HTTPS, not SSH.
        </div>
      </div>
    </div>
  );
}
